<script setup lang="ts">
  import { computed } from 'vue';
  import PosFloorPlan from '@/components/pages/restaurant/pos/PosFloorPlan.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import { usePosTablesView } from '@/composition/use-pos-tables-view';
  import { buildPosTableViews } from '@/domain/pos/table-views';
  import {
    attachReservationHints,
    getFloorReservationCandidates,
  } from '@/domain/reservations/pos-table-hints';
  import { OrderType } from '@/enums/OrderEnums';
  import type { IReservation } from '@/interfaces/IReservation';
  import type { IPosTableView } from '@/interfaces/IPosTableView';
  import { useOrdersStore } from '@/stores/orders.store';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  const props = defineProps<{
    date: string;
    today: string;
    reservations: IReservation[];
    occupancyReservations: IReservation[];
    now: Date;
    toleranceMin: number;
    timeZone: string;
  }>();
  const emit = defineEmits<{
    (event: 'select', id: number): void;
    (event: 'create', value: { tableIds: number[] }): void;
  }>();
  const restaurant = useRestaurantStore();
  const orders = useOrdersStore();
  const { sectors, planAvailable } = usePosTablesView();
  const tables = computed(
    () =>
      attachReservationHints(
        buildPosTableViews(
          restaurant.tables,
          props.date === props.today
            ? orders.pendingOrdersByType[OrderType.Seat] ?? []
            : [],
          ''
        ),
        getFloorReservationCandidates(
          props.reservations,
          props.occupancyReservations
        ),
        props.now,
        props.toleranceMin,
        props.timeZone
      ) ?? []
  );
  const legend = ['free', 'reserved', 'occupied', 'toCollect', 'paid'];
  function select(table: IPosTableView) {
    if (table.reservation) emit('select', table.reservation.id);
    else emit('create', { tableIds: [table.id] });
  }
</script>

<template>
  <div class="reservations-floor">
    <PosUiEmptyState
      v-if="!planAvailable"
      icon="floor-plan"
      :title="$t('posTables.planCtaTitle')"
      :description="$t('posTables.planCtaText')"
    >
      <template #action
        ><PosUiButton
          icon="floor-plan"
          @click="
            $router.push(`/${restaurant.restaurant.stringId}/tables/plan`)
          "
          >{{ $t('posTables.planCtaButton') }}</PosUiButton
        ></template
      >
    </PosUiEmptyState>
    <template v-else>
      <div class="floor-legend">
        <span v-for="state in legend" :key="state"
          ><i :class="state" />{{
            $t(`reservations.floor.legend.${state}`)
          }}</span
        >
      </div>
      <PosFloorPlan :sectors="sectors" :tables="tables" @table-click="select" />
    </template>
  </div>
</template>

<style scoped lang="scss">
  .reservations-floor {
    @apply tw-min-h-full tw-p-4;
    .floor-legend {
      @apply tw-mb-4 tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-2 tw-text-xs tw-text-neutral-600;
      > span {
        @apply tw-flex tw-items-center tw-gap-1.5;
      }
      i {
        @apply tw-block tw-h-3 tw-w-3 tw-rounded tw-border tw-border-solid tw-border-neutral-300 tw-bg-white;
      }
      .reserved {
        @apply tw-border-dashed tw-border-sky-500 tw-bg-sky-50;
      }
      .occupied {
        @apply tw-border-amber-400 tw-bg-amber-100;
      }
      .toCollect {
        @apply tw-border-sky-400 tw-bg-sky-100;
      }
      .paid {
        @apply tw-border-emerald-400 tw-bg-emerald-100;
      }
    }
  }
</style>
